package br.com.vvrefeicoes.portal.feature.forms

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.layout.appLayoutMetrics
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allowsAny
import br.com.vvrefeicoes.portal.feature.notifications.RichBody
import com.composables.icons.lucide.ArrowLeft
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.X

private val ChoiceAnswerColumnWidth = 72.dp

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun FormReadScreen(
    formId: String,
    onBack: () -> Unit,
) {
    val appGraph = LocalAppGraph.current
    val layout = appLayoutMetrics()
    val scope = rememberCoroutineScope()
    val controller =
        remember(formId, appGraph.formsRepository, scope) {
            FormReadController(appGraph.formsRepository, scope, formId)
        }
    LaunchedEffect(controller) { controller.init() }
    val form = controller.form
    val permissions = rememberSessionPermissions()
    val canView = form?.sectors?.hasAllowedFormSector(permissions) == true
    val canApprove =
        form?.sectors?.hasAllFormSectors(permissions) == true &&
            permissions.allowsAny(PermissionKeys.FORMULARIOS_APPROVE)
    var rejecting by remember { mutableStateOf(false) }
    var rejectionReason by remember { mutableStateOf("") }
    var rejectionError by remember { mutableStateOf<String?>(null) }

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = layout.pagePadding,
        verticalArrangement = Arrangement.spacedBy(layout.pageGap),
        footer = { AppFooter() },
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(
                text = if (canView) form.title else "Formulário",
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.headlineMedium,
            )
            AppButton(
                text = "Voltar",
                onClick = onBack,
                variant = PortalButtonVariant.Text,
                leadingIcon = Lucide.ArrowLeft,
            )
        }

        if (!controller.loading && form != null && canView) {
            controller.errorMessage?.let { message ->
                AppBanner(
                    text = message,
                    onDismiss = { controller.dismissError() },
                    isError = true,
                )
            }
        }

        if (controller.loading) {
            Box(modifier = Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
            return@AppScrollColumn
        }

        if (form == null || !canView) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = AppShapes.large,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Text(
                    text = controller.errorMessage ?: "Formulário não encontrado.",
                    modifier = Modifier.padding(AppSpacing.space16),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            return@AppScrollColumn
        }

        FormReadContent(
            description = form.description,
            sectors = form.sectors,
            createdBy = form.createdBy,
            approvedBy = form.approvedBy,
            updatedAt = form.updatedAt,
            sections = form.sections,
            signatures = form.signatures,
            actions =
                if (form.status == FormStatus.PENDING_APPROVAL && canApprove) {
                    {
                        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                            AppButton(
                                text = "Rejeitar",
                                onClick = { rejecting = true },
                                variant = PortalButtonVariant.Danger,
                                leadingIcon = Lucide.X,
                            )
                            AppButton(
                                text = "Aprovar",
                                onClick = { controller.approve(onBack) },
                                enabled = !controller.busy,
                                variant = PortalButtonVariant.Filled,
                                leadingIcon = Lucide.Check,
                            )
                        }
                    }
                } else {
                    null
                },
        )
    }

    if (rejecting) {
        AppModalCard(
            title = "Rejeitar formulário",
            onDismiss = {
                rejecting = false
                rejectionError = null
            },
        ) {
            AppTextField(
                value = rejectionReason,
                onValueChange = {
                    rejectionReason = it
                    rejectionError = null
                },
                label = "Motivo da rejeição",
                singleLine = false,
                modifier = Modifier.fillMaxWidth(),
            )
            rejectionError?.let { message ->
                Text(
                    text = message,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error,
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = "Cancelar",
                    onClick = {
                        rejecting = false
                        rejectionError = null
                    },
                    variant = PortalButtonVariant.Text,
                )
                AppButton(
                    text = "Rejeitar",
                    onClick = {
                        if (rejectionReason.isBlank()) {
                            rejectionError = "Informe o motivo da rejeição."
                        } else {
                            controller.reject(rejectionReason) {
                                rejecting = false
                                rejectionReason = ""
                                onBack()
                            }
                        }
                    },
                    enabled = !controller.busy,
                    variant = PortalButtonVariant.Secondary,
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun FormReadContent(
    description: String,
    sectors: List<String>,
    createdBy: String,
    approvedBy: String?,
    updatedAt: String,
    sections: List<FormSection>,
    signatures: List<FormSignature> = emptyList(),
    actions: (@Composable () -> Unit)? = null,
) {
    val layout = appLayoutMetrics()
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(layout.pageGap),
    ) {
        if (sectors.isNotEmpty()) {
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                sectors.forEach { sector ->
                    val style = FormSector.fromLabel(sector)?.let(::formSectorStyle)
                    Surface(
                        shape = CircleShape,
                        color = style?.containerColor ?: MaterialTheme.colorScheme.primaryContainer,
                    ) {
                        Text(
                            text = sector,
                            modifier =
                                Modifier.padding(
                                    horizontal = AppSpacing.space12,
                                    vertical = AppSpacing.space4,
                                ),
                            style = MaterialTheme.typography.labelMedium,
                            color = style?.contentColor ?: MaterialTheme.colorScheme.onPrimaryContainer,
                        )
                    }
                }
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Text(
                text =
                    buildList {
                        add("Criado por $createdBy")
                        approvedBy?.let { value -> add("Aprovado por $value") }
                        add(formUpdatedAtLabel(updatedAt))
                    }.joinToString(" · "),
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            actions?.invoke()
        }

        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = AppShapes.large,
            color = MaterialTheme.colorScheme.surface,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
                verticalArrangement = Arrangement.spacedBy(layout.cardGap),
            ) {
                Text(
                    text = "Descrição",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    text = description,
                    style = MaterialTheme.typography.bodyMedium,
                )
            }
        }

        sections.forEachIndexed { index, section ->
            ReadSectionCard(
                section = section,
                index = index + 1,
            )
        }

        if (signatures.isNotEmpty()) {
            ReadSignatures(signatures)
        }
    }
}

@Composable
private fun ReadSignatures(signatures: List<FormSignature>) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space24)) {
        signatures.chunked(2).forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space24),
                verticalAlignment = Alignment.Top,
            ) {
                if (row.size == 1) {
                    Spacer(modifier = Modifier.weight(0.5f))
                }
                row.forEach { signature ->
                    SignatureBlock(
                        label = signature.label,
                        modifier = Modifier.weight(1f),
                    )
                }
                if (row.size == 1) {
                    Spacer(modifier = Modifier.weight(0.5f))
                }
            }
        }
    }
}

@Composable
private fun SignatureBlock(
    label: String,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.padding(top = AppSpacing.space24),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(modifier = Modifier.height(48.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth(),
            color = MaterialTheme.colorScheme.outline,
        )
        Text(
            text = label,
            modifier = Modifier.padding(top = AppSpacing.space8),
            style = MaterialTheme.typography.labelMedium,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun ReadSectionCard(
    section: FormSection,
    index: Int,
) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            ) {
                Surface(
                    modifier = Modifier.size(32.dp),
                    shape = AppShapes.large,
                    color = MaterialTheme.colorScheme.primary,
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(
                            text = index.toString(),
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onPrimary,
                        )
                    }
                }
                Text(
                    text = section.title,
                    style = MaterialTheme.typography.titleMedium,
                )
            }
            section.items.forEach { item ->
                when (item.type) {
                    FormItemType.TEXT ->
                        RichBody(html = item.textHtml, modifier = Modifier.fillMaxWidth())
                    FormItemType.QUESTION ->
                        item.question?.let { ReadQuestion(it) }
                    FormItemType.CALCULATION_TABLE ->
                        item.choiceTable?.let {
                            ReadChoiceTable(
                                table = it,
                                showWeights = true,
                                total = calculateCalculationTableScore(it, emptyMap()),
                            )
                        }
                    FormItemType.QUESTION_TABLE ->
                        item.questionTable?.let { ReadQuestionTable(it) }
                    FormItemType.QUESTION_MATRIX ->
                        item.questionMatrix?.let { ReadQuestionMatrix(it) }
                    FormItemType.TEXT_TABLE ->
                        item.textTable?.let { ReadTextTable(it) }
                }
            }
        }
    }
}

@Composable
private fun ReadQuestion(question: FormQuestion) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
            Text(
                text = question.title,
                style = MaterialTheme.typography.titleSmall,
            )
            if (question.required) {
                Text(
                    text = "*",
                    style = MaterialTheme.typography.titleSmall,
                    color = MaterialTheme.colorScheme.error,
                )
            }
        }
        when (question.type) {
            FormQuestionType.TEXT ->
                ReadAnswerBox(
                    text = "Digite sua resposta",
                    minHeight = 56.dp,
                )
            FormQuestionType.SINGLE_CHOICE ->
                ReadChoiceOptions(
                    options = question.options,
                    multiple = false,
                )
            FormQuestionType.MULTIPLE_CHOICE ->
                ReadChoiceOptions(
                    options = question.options,
                    multiple = true,
                )
            FormQuestionType.FILE ->
                ReadFileBox(question.acceptedFiles)
        }
    }
}

@Composable
private fun ReadAnswerBox(
    text: String,
    minHeight: androidx.compose.ui.unit.Dp,
    modifier: Modifier = Modifier.fillMaxWidth(),
) {
    Box(modifier = modifier) {
        Text(
            text = text,
            modifier = Modifier.fillMaxWidth().heightIn(min = minHeight).padding(AppSpacing.space16),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        HorizontalDivider(
            modifier = Modifier.align(Alignment.BottomStart).fillMaxWidth(),
            color = MaterialTheme.colorScheme.outline,
        )
    }
}

@Composable
private fun ReadChoiceOptions(
    options: List<FormOption>,
    multiple: Boolean,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        options.forEach { option ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                if (multiple) {
                    Checkbox(
                        checked = false,
                        onCheckedChange = null,
                        enabled = false,
                        colors =
                            CheckboxDefaults.colors(
                                disabledUncheckedColor = MaterialTheme.colorScheme.outline,
                            ),
                    )
                } else {
                    RadioButton(
                        selected = false,
                        onClick = null,
                        enabled = false,
                        colors =
                            RadioButtonDefaults.colors(
                                disabledUnselectedColor = MaterialTheme.colorScheme.outline,
                            ),
                    )
                }
                Text(
                    text = option.label,
                    style = MaterialTheme.typography.bodyMedium,
                )
            }
        }
    }
}

@Composable
private fun ReadFileBox(acceptedFiles: Set<FormFileType>) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        ReadAnswerBox(
            text = "Selecionar arquivo",
            minHeight = 56.dp,
        )
        Text(
            text = "Arquivos aceitos: ${acceptedFiles.joinToString { it.label }}",
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun ReadChoiceTable(
    table: FormChoiceTable,
    showWeights: Boolean,
    total: Int? = null,
) {
    val layout = appLayoutMetrics()
    val showWeight = showWeights && table.questions.any { it.weight != 1 }
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Text(
                text = table.title,
                modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
                style = MaterialTheme.typography.titleSmall,
            )
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .padding(horizontal = layout.cardPadding, vertical = AppSpacing.space8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "Pergunta",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (showWeight) {
                    Text(
                        text = "Peso",
                        modifier = Modifier.width(ChoiceAnswerColumnWidth),
                        style = MaterialTheme.typography.labelMedium,
                        textAlign = TextAlign.Center,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                table.columns.forEach { column ->
                    Text(
                        text = column.label,
                        modifier = Modifier.width(ChoiceAnswerColumnWidth),
                        style = MaterialTheme.typography.labelMedium,
                        textAlign = TextAlign.Center,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            table.questions.forEachIndexed { index, question ->
                if (index > 0) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
                Row(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .padding(horizontal = layout.cardPadding, vertical = AppSpacing.space8),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = question.title,
                        modifier = Modifier.weight(1f),
                        style = MaterialTheme.typography.bodyMedium,
                    )
                    if (showWeight) {
                        Text(
                            text = question.weight?.toString() ?: "—",
                            modifier = Modifier.width(ChoiceAnswerColumnWidth),
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                        )
                    }
                    repeat(table.columns.size) {
                        Box(
                            modifier = Modifier.width(ChoiceAnswerColumnWidth),
                            contentAlignment = Alignment.Center,
                        ) {
                            RadioButton(
                                selected = false,
                                onClick = null,
                                enabled = false,
                                colors =
                                    RadioButtonDefaults.colors(
                                        disabledUnselectedColor = MaterialTheme.colorScheme.outline,
                                    ),
                            )
                        }
                    }
                }
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .padding(horizontal = layout.cardPadding, vertical = AppSpacing.space8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "Total",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleSmall,
                )
                if (showWeight) {
                    Spacer(modifier = Modifier.width(ChoiceAnswerColumnWidth))
                }
                Text(
                    text = total?.toString() ?: "—",
                    modifier = Modifier.width(ChoiceAnswerColumnWidth * table.columns.size),
                    style = MaterialTheme.typography.titleSmall,
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

@Composable
private fun ReadQuestionTable(table: FormQuestionTable) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Text(
                text = table.title,
                style = MaterialTheme.typography.titleSmall,
            )
            Column(modifier = Modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                ) {
                    repeat(table.columnCount) { column ->
                        ReadTableHeaderCell(
                            text = table.header(column),
                            modifier = Modifier.weight(1f).fillMaxHeight(),
                        )
                    }
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                repeat(table.rowCount - 1) { dataRow ->
                    val row = dataRow + 1
                    Row(
                        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                    ) {
                        repeat(table.columnCount) { column ->
                            ReadAnswerBox(
                                text = "Digite sua resposta",
                                minHeight = 56.dp,
                                modifier = Modifier.weight(1f).fillMaxHeight(),
                            )
                        }
                    }
                    if (row < table.rowCount - 1) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
        }
    }
}

@Composable
private fun ReadQuestionMatrix(table: FormQuestionMatrix) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Text(
                text = table.title,
                style = MaterialTheme.typography.titleSmall,
            )
            Column(modifier = Modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                ) {
                    ReadTableHeaderCell(
                        text = "",
                        modifier = Modifier.weight(1f).fillMaxHeight(),
                    )
                    table.columns.forEach { column ->
                        ReadTableHeaderCell(
                            text = column.label,
                            modifier = Modifier.weight(1f).fillMaxHeight(),
                        )
                    }
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                table.rows.forEachIndexed { index, row ->
                    Row(
                        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                    ) {
                        ReadTableHeaderCell(
                            text = row.label,
                            modifier = Modifier.weight(1f).fillMaxHeight(),
                        )
                        table.columns.forEach {
                            ReadAnswerBox(
                                text = "Digite sua resposta",
                                minHeight = 56.dp,
                                modifier = Modifier.weight(1f).fillMaxHeight(),
                            )
                        }
                    }
                    if (index < table.rows.lastIndex) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
        }
    }
}

@Composable
private fun ReadTableHeaderCell(
    text: String,
    modifier: Modifier,
) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(0.dp),
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.45f),
    ) {
        Text(
            text = text,
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun ReadTextTable(table: FormTextTable) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Text(
                text = table.title,
                style = MaterialTheme.typography.titleSmall,
            )
            Column(modifier = Modifier.fillMaxWidth()) {
                repeat(table.rowCount) { row ->
                    Row(
                        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
                    ) {
                        repeat(table.columnCount) { column ->
                            if (row == 0) {
                                ReadTableHeaderCell(
                                    text = table.cell(row, column),
                                    modifier = Modifier.weight(1f).fillMaxHeight(),
                                )
                            } else {
                                ReadTableBodyCell(
                                    text = table.cell(row, column),
                                    modifier = Modifier.weight(1f).fillMaxHeight(),
                                )
                            }
                        }
                    }
                    if (row < table.rowCount - 1) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
        }
    }
}

@Composable
private fun ReadTableBodyCell(
    text: String,
    modifier: Modifier,
) {
    Surface(
        modifier = modifier,
        color = MaterialTheme.colorScheme.surface,
    ) {
        Text(
            text = text,
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            style = MaterialTheme.typography.bodyMedium,
        )
    }
}
